import Select, { AriaOnFocus } from 'react-select';
import classNames from 'classnames';
import { LessonMode } from '../../Constants';
import { register } from '../../i18n';

import lessonManager from '../managers/LessonManager';
import multichoiceManager from '../managers/MultichoiceManager';

import dropdownService from './DropdownService';
import promptService from './PromptService';
import questionService from './QuestionService';
import responseService from './ResponseService';
import textQuestionUtilsService from './TextQuestionUtilsService';
import utilsService from './UtilsService';

import { confirmDialog } from '../components/dialogs';
import FeedbackIcon from '../components/FeedbackIcon';
import HtmlComponent from '../components/HtmlComponent';
import PrintDropdownOptions from '../components/PrintDropdownOptions';

const t = register('GlobalQuestionLabels');


export default class ClozeMultichoiceService {
  static normalizeChoiceText = (value) => {
    const { stripZeroWidthChars, stripTagsAndEntities } = utilsService;
    const stripPWrap = utilsService.stripWrappingParagraphTags;
    return stripTagsAndEntities(stripZeroWidthChars(stripPWrap(value || ''))).trim();
  }

  static getSelectedMultiSelectOptions = ({
    customDropdownOptions = [],
    currentUserInput,
    model
  } = {}) => {
    const selectedAnswerIds = new Set(currentUserInput?.answerIds || []);
    if (currentUserInput?.answerId) {
      selectedAnswerIds.add(currentUserInput.answerId);
    }

    const selectedTexts = new Set();
    (currentUserInput?.selections || []).forEach((selection) => {
      const normalized = ClozeMultichoiceService.normalizeChoiceText(selection?.text);
      if (normalized) {
        selectedTexts.add(normalized);
      }
    });

    const rawText = typeof currentUserInput?.text === 'string' ? currentUserInput.text : '';
    if (rawText && selectedAnswerIds.size === 0 && selectedTexts.size === 0) {
      rawText.split(/\s*,\s*/).forEach((part) => {
        const normalized = ClozeMultichoiceService.normalizeChoiceText(part);
        if (normalized) {
          selectedTexts.add(normalized);
        }
      });
    }

    return customDropdownOptions.filter((option) => {
      if (!option || option.value === 'prependedSelectLabel' || option.dataId === 'prependedSelectLabel') {
        return false;
      }
      if (selectedAnswerIds.has(option.value) || selectedAnswerIds.has(option.originalData?.id)) {
        return true;
      }
      const optionLabel = (model?.responseFormat === 'html') ? option.viewLabel : option.label;
      return selectedTexts.has(ClozeMultichoiceService.normalizeChoiceText(optionLabel));
    });
  }

  /** @param {import('html-react-parser').DOMNode} domNode */
  static renderClozeMultichoiceBlankSection = (domNode, {
    dataId,
    forceUpdate = () => {}, // empty function by default. needs to be passed by ClozeMultichoice
    lessonElementId,
    lessonElementState,
    // modalBody,
    modalLeft,
    modalTop,
    model,
    readOnly,
    setModalBody,
    uiState,
    userInputMap,
    noSubmit
  } = {}) => {
    const _renderClozeMultichoiceBlankSectionFn = (domNode, {
      dataId,
      forceUpdate = () => {}, // empty function by default. needs to be passed by ClozeMultichoice
      lessonElementId,
      lessonElementState,
      model,
      readOnly,
      uiState,
      userInputMap
    } = {}) => {
      dataId = dataId || domNode?.attribs['data-id'];

      if (!dataId) {
        return null;
      }

      // There is an issue with copied cloze multichoice item bank items where the dataId does not get updated.
      // Which means the multichoiceDropdwonItems map didn't know the difference between all of the dropdowns.
      // Concatenating the dataId and the lessonElementId is hopefully a temporary solution to fix this problem.
      const dropdownId = dataId + lessonElementId;

      if (!multichoiceManager.hasMultichoiceItems(dropdownId)) {
        // Only fetch and set the items if they haven't been set already
        const customDropdownOptions = dropdownService.getDropdownOptions({
          dataId,
          model
        });
        multichoiceManager.setMultichoiceItems(dropdownId, customDropdownOptions);
      }

      const customDropdownOptions = multichoiceManager.getMultichoiceItems(dropdownId);

      if (!customDropdownOptions?.length) {
        return undefined;
      }

      const showAnswerFeedback = uiState?.showAnswerFeedback || false;

      let nodeClassNames = domNode?.attribs?.['class'];
      nodeClassNames = classNames(nodeClassNames, {
        correct: false, // we do not want students seeing this className in the dev tools
        feedback: showAnswerFeedback,
        incorrect: false // we do not want students seeing this className in the dev tools
      }, `${model.responseFormat || ''}`);

      // TODO remove // let editorClassName = model.responseFormat === 'html' ? 'cke_topspace ' : '';

      const responseAnswerPrompt = promptService.getCurrentPrompt({
        dataId,
        lessonElementState,
        model,
        promptType: 'responseAnswerPrompt',
      }) || {};

      const { id, text, ...responseAnswerPromptRest } = responseAnswerPrompt;

      const isResponseAnswerPromptEmpty = !Object.keys(responseAnswerPrompt)?.length;
      if (!isResponseAnswerPromptEmpty) {
        userInputMap?.set(dataId, {
          ...userInputMap.get?.(dataId),
          dataId,
          text: responseAnswerPrompt?.text?.trim?.()/* || clozeText */,
          ...responseAnswerPromptRest
        });
      }

      let currentUserInputValue = questionService.getCurrentUserInputValue({ dataId, lessonElementState, userInputMap });
      if (currentUserInputValue?.includes?.('data-mathml')) {
        currentUserInputValue = utilsService.findMathInput(currentUserInputValue);
      }

      let selectedDropdownOption = customDropdownOptions.find((option) => {
        const currentUserInput = lessonElementState?.currentResponseAnswer?.userInputMap?.get(dataId);
        if (currentUserInput?.answerId && (option.value === currentUserInput.answerId || option.originalData?.id === currentUserInput.answerId)) {
          return true;
        }
        if (option.value === currentUserInputValue || option.originalData?.id === currentUserInputValue) {
          return true;
        }
       
        let optionLabel = (model?.responseFormat === 'html') ? option.viewLabel : option.label;
        if (optionLabel?.includes?.('data-mathml')) {
          optionLabel = utilsService.findMathInput(optionLabel);
        }

        const { stripZeroWidthChars } = utilsService;
        const stripPWrap = utilsService.stripWrappingParagraphTags;

        return stripZeroWidthChars(stripPWrap(optionLabel)) === stripZeroWidthChars(stripPWrap(currentUserInputValue));
      });
      if (selectedDropdownOption === undefined) {
        selectedDropdownOption = null;
      }

      const currentMultiInput = lessonElementState?.currentResponseAnswer?.userInputMap?.get(dataId)
        || userInputMap?.get?.(dataId);
      const isMultiSelect = promptService.isMultiSelectDropdownPrompt({
        dataId,
        model,
        userInput: currentMultiInput
      });
      const selectedDropdownOptions = isMultiSelect
        ? ClozeMultichoiceService.getSelectedMultiSelectOptions({
          customDropdownOptions,
          currentUserInput: currentMultiInput,
          model
        })
        : [];

      // TODO unused
      // const promptAnswerStatus = promptService.getAnswerStatusForCurrentPrompt({
      //   currentUserInputValue,
      //   dataId,
      //   model
      // });

      const { customComponents, customDropdownStyles } = dropdownService.getDropdownConfig({
        dataId, isMultiSelect, modalLeft, modalTop, model, showAnswerFeedback
      });
      const { playerMode } = lessonManager;

      if (lessonManager.playerMode === LessonMode.PRINT_PREVIEW) {
        return (
          <div className={classNames('cloze-blank-section cloze-section input-section multichoice-section print-dropdown-blank', nodeClassNames)}
            data-id={dataId}>
            {model.numberedBlanks && <div className='list-number' />}
            <PrintDropdownOptions options={customDropdownOptions} />
          </div>
        );
      }

      return (
        <div key={/* model.mathText ? `math-${dataId}` : */dataId} aria-label={t('selectAnswer')}
          className={classNames('cloze-blank-section cloze-section input-section multichoice-section', {
            'is-multi-select': isMultiSelect
            // TODO remove // editorClassName
            // 'cloze-math-blank-section': !!model.mathText
          }, nodeClassNames)}
          data-id={dataId}>
          {/* <FeedbackIcon answerId={dataId} lessonElementId={lessonElementId} /> */}
          {/* <span className={`standalone-input-icon ${nodeClassNames}${numberedCircleClassName} ${playerMode} ${promptAnswerStatus}`} /> */}
          <div  aria-label={t('selectAnswer')} className={classNames('cloze-section-input-wrapper', {
            'empty-input': isMultiSelect ? selectedDropdownOptions.length === 0 : !currentUserInputValue,
          }, nodeClassNames, playerMode)} >
            {model.numberedBlanks && <div className='list-number' />}
            <Select
              tabIndex={0}
              {...((model && !utilsService.isNullOrEmpty(model.mathText)) ? {
                autoFocus: true,
                // TODO remove // menuIsOpen: true,
                openMenuOnFocus: true
              } : {})}
              
              className='react-player-select-container'
              classNamePrefix='player-select'
              closeMenuOnSelect={!isMultiSelect}
              components={customComponents}
              formatOptionLabel={(data) => {
                let dataLabel = data.viewLabel;
                if (dataLabel?.startsWith?.('<menclose')) {
                  dataLabel = `<math>${dataLabel}</math>`;
                } else 
                return <HtmlComponent htmlStr={dataLabel} />;
              }}
              hideSelectedOptions={false}
              isClearable={false}
              isDisabled={isMultiSelect ? (readOnly && !showAnswerFeedback) : (showAnswerFeedback || readOnly)}
              isMulti={isMultiSelect}
              isSearchable={false}
              lessonElementId={lessonElementId}
              lessonElementState={lessonElementState}
              maxMenuHeight={220}
              menuPlacement='auto'
              menuPortalTarget={document.body}
              menuPosition={isMultiSelect ? 'fixed' : 'absolute'}
              menuShouldScrollIntoView={false}
              model={model}
              onBlur={() => setModalBody?.(undefined)}
              onChange={async (data) => {
                if (showAnswerFeedback || readOnly) {
                  return;
                }
                if (isMultiSelect) {
                  const selectedOptions = (Array.isArray(data) ? data : []).filter((option) => (
                    option && option.dataId !== 'prependedSelectLabel' && option.value !== 'prependedSelectLabel'
                  ));
                  const maxSelections = promptService.getDropdownMaxSelections({ dataId, model });
                  if (maxSelections && selectedOptions.length > maxSelections) {
                    await confirmDialog({
                      title: `${utilsService.maxSelected()}`,
                      text: `${utilsService.deselect()}`,
                      showCancelButton: false,
                      confirmButtonText: `${utilsService.confirmOk()}`
                    });
                    return;
                  }
                }
                const clearedPlaceholder = !data
                  || (!isMultiSelect && (data?.dataId === 'prependedSelectLabel' || data?.value === 'prependedSelectLabel'));
                if (!clearedPlaceholder) {
                  responseService.textChangeHandler(
                    data, lessonElementId, { dataId, lessonElementState }
                  );
                  setModalBody?.(undefined);
                } else {
                  setModalBody?.(undefined);
                }
              }}
             
              options={customDropdownOptions}
              placeholder={`${t('choose')}...`}
              showAnswerFeedback={showAnswerFeedback}
              styles={customDropdownStyles}
              value={isMultiSelect ? selectedDropdownOptions : selectedDropdownOption}
              aria-label={t('enterHere')}
            />
          </div>
          <FeedbackIcon
            {...lessonElementState.currentResponseAnswer}
            {...textQuestionUtilsService.getFeedbackIconOptionsForDecoratingTextInputs()}
            answerId={dataId} />
        </div>
      );
    };
    return _renderClozeMultichoiceBlankSectionFn(domNode, {
      dataId,
      forceUpdate,
      lessonElementId,
      lessonElementState,
      model,
      readOnly,
      uiState,
      userInputMap,
      noSubmit
    });
  };
}
